import { toast } from 'react-toastify';
import { combineEpics, ofType } from "redux-observable";
import { from, of, concat } from "rxjs";
import { map, catchError, mergeMap, debounceTime, withLatestFrom, concatMap, tap, distinctUntilChanged, switchMap } from "rxjs/operators";
import { importWellSurveyData } from "../../../services/calculations/calculation-output/wellSurvey.service";
import { getTailPipeTZ } from "../../../services/calculations/calculation-output/basicCalculation.service";
import { setLoading, setError, setWellSurveyData } from "../../../store/calculation/calculation-output/wellSurveySlice";
import { RootState } from '../../../store/ConfigureStore';
import * as wellSurvey from "../../../../src/store/calculation/calculation-output/wellSurveySlice";
import { wellSurveyService } from '../../../services/calculations/calculation-output/wellSurvey.service';

const fetchWellSurveyEpic = (action$: any) => {
    return action$.pipe(
        ofType("wellSurvey/fetchData"), // Triggered by the action type
        mergeMap((action: any) => {
            const { inputData } = action.payload;
            return from(importWellSurveyData(inputData)).pipe(
                tap((response: any) => {
                    toast.success('Successfully imported survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#4CAF50',
                            color: '#fff'
                        }
                    });
                }),
                map((response) => {
                    const processedData = response.map((item: any, index: number) => ({
                        ...item,
                        id: item.id || index + 1 // Use existing id or create one
                    }));
                    return { type: "wellSurvey/fetchDataSuccess", payload: processedData };
                }),
                catchError((error) => {
                    toast.error('Failed to import survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#f44336',
                            color: '#fff'
                        }
                    });
                    return of(setError(error.message));
                })
            );
        })
    );
};



const clearWellSurveyEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("wellSurvey/clearData"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject)),
            state$.pipe(map((state: RootState) => state?.wellSurvey?.isWellSurveyDataPersisted))
        ),
        mergeMap(([actionData, selectedProject, isDataPersisted]) => {
            // If data is not persisted in DB, just clear locally
            if (!isDataPersisted) {
                toast.success('Successfully cleared survey data', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    style: {
                        background: '#4CAF50',
                        color: '#fff'
                    }
                });
                return of({ type: "wellSurvey/clearDataSuccess", payload: null });
            }

            // If data is persisted, clear from DB
            let url = window.location.href;
            let caseIdFromUrl: any = null;
            const caseIdRegex = /caseID=([\w-]+)/;
            const match = url.match(caseIdRegex);
            if (match && match[1]) {
                caseIdFromUrl = match[1];
            }
            const projectId = caseIdFromUrl
                ? caseIdFromUrl
                : (actionData?.payload?.projectId
                    ? actionData?.payload?.projectId
                    : selectedProject?.projectID || selectedProject?.caseID);
                    
            if (!projectId) {
                return of({ type: "wellSurvey/clearExistingDataError", payload: "No project ID found" });
            }

            return from(wellSurveyService.clearWellSurveyData(projectId)).pipe(
                tap(() => {
                    toast.success('Successfully cleared survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#4CAF50',
                            color: '#fff'
                        }
                    });
                }),
                mergeMap(() => concat(
                    of({ type: "wellSurvey/clearDataSuccess", payload: null }),
                    of({ type: "wellSurvey/fetchExistingData", payload: {"projectId": caseIdFromUrl}}),
                )),
                catchError((error) => {
                    toast.error('Failed to clear survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#f44336',
                            color: '#fff'
                        }
                    });
                    return of(setError(error.message));
                })
            );
        })
    );
}


const fetchExistingWellSurveyEpic = (action$: any, state$: any) =>
    action$.pipe(
        ofType("wellSurvey/fetchExistingData"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject))
        ),
        switchMap(([actionData, selectedProject]) => {
            const projectId: string = actionData?.payload?.projectId
                ? actionData?.payload?.projectId
                : selectedProject?.projectID || selectedProject?.caseID;

            if (!projectId) {
                return of({ type: "wellSurvey/fetchExistingDataError", payload: "No project ID found" });
            }

            return from(wellSurveyService.getExistingWellSurveyData(projectId)).pipe(
                tap((response) => { }),
                mergeMap((response) => concat(
                    of({
                        type: "wellSurvey/fetchExistingDataSuccess",
                        payload: response
                    }),
                    of({ type: "wellSurvey/setWellSurveyDataPersisted", payload: response && response.length > 0 })
                )),
                catchError((error) => {
                    console.error('Error fetching well survey data:', error);
                    return of({
                        type: "wellSurvey/fetchExistingDataError",
                        payload: error.message
                    });
                })
            );
        })
    );


const calculateMaxDLS = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("wellSurvey/setWellKOPOverride", "wellSurvey/setWellSurveyData"),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellSurvey.KOPOverride)),
            state$.pipe(map((state: RootState) => state?.wellSurvey.wellSurveyData))
        ),
        tap(([action, kopOverride, wellSurveyData]: [{ type: string }, number | undefined, any[]]) => {
        }),
        map(([actionData, kopOverride, wellSurvey]) => {
            if (!wellSurvey || !kopOverride) {
                return 0;
            }

            const kopIndex = wellSurvey.findIndex((x: any) => x?.md === kopOverride);


            if (kopIndex === -1) {

                return 0;
            }

            const arr = wellSurvey.slice(0, kopIndex);
            const maxDLS = Math.max(...arr.map((o: any) => o.dls || 0));

            return maxDLS;
        }),

        map(maxDLS => wellSurvey.setMaxDLSAboveKOP(maxDLS))
    );
};

const calculateDepthMaxDLS = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("wellSurvey/setMaxDLSAboveKOP"),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellSurvey.MaxDLSAboveKOP)),
            state$.pipe(map((state: RootState) => state?.wellSurvey.wellSurveyData))
        ),
        tap(([action, maxDLS, wellSurveyData]) => {
        }),
        map(([actionData, maxDLS, wellSurvey]) => {
            try {
                if (wellSurvey?.length > 0 && maxDLS) {
                    const mdIndex = wellSurvey.findIndex((x: any) => x.dls === maxDLS);
                    if (mdIndex === -1) return 0;

                    const depthMD = wellSurvey[mdIndex]?.md;
                    return depthMD;
                }
                return 0;
            } catch (error) {
                console.error('Error calculating DepthMaxDLS:', error);
                return 0;
            }
        }),

        map(depthMD => wellSurvey.setDepthOfMaxDLS(depthMD))
    );
};

const calculateTransitionZone = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("wellSurvey/setWellKOPOverride", "wellSurvey/setWellSurveyData"),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellSurvey.wellSurveyData)),
            state$.pipe(map((state: RootState) => state?.wellSurvey.KOPOverride))
        ),
        tap(([action, wellSurveyData, kopOverride]) => {
        }),
        map(([actionData, wellSurvey, kopOverride]) => {
            try {
                if (wellSurvey?.length > 0 && kopOverride) {
                    const mdIndex = wellSurvey.findIndex((x: any) => x?.md === kopOverride);
                    const transitionZone = mdIndex === -1 ? 0 : wellSurvey[mdIndex - 1]?.md;

                    return transitionZone;
                }
                return 0;
            } catch (error) {
                console.error('Error calculating Transition Zone:', error);
                return 0;
            }
        }),

        map((tzValue: number) => wellSurvey.setTransitionZone(tzValue))
    );
};

interface TailPipe {
    tailPipeDesander: number;
    transitionZone: number;
    tailPipeBttm: number;
}

const calculateTailPipeTZ = (action$: any, state$: any) => {
    return action$.pipe(
        // Only trigger on specific actions that should cause recalculation
        ofType(
            "wellSurvey/setTailPipeDesander",
            "wellSurvey/setTailPipeBottom",
            "wellSurvey/setTransitionZone",
        ),
        // Add debounce to prevent rapid firing
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.calculations.schematicData)),
            state$.pipe(map((state: RootState) => state.wellSurvey.TransitionZone)),
            state$.pipe(map((state: RootState) => state.schematic.ESPEquipmentsForBelow))
        ),
        mergeMap(([action, schematicData, transitionZone, equipmentForBelow]) => {
            try {
                if (schematicData?.setDepth > 0) {
                    const tailPipeDesander = schematicData.setDepth;
                    let tailPipeBttm = 0;
                    const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                    const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                    const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);
                    const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                        ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                        : 0;
                    const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;

                    if (tailPipeLengthValue === 0) {
                        tailPipeBttm = bottomOfEspSensorVal + equipmentBelowLength;
                    } else {
                        tailPipeBttm = bottomOfEspSensorVal + tailPipeLength;
                    }

                    const postData: TailPipe = {
                        tailPipeDesander,
                        transitionZone,
                        tailPipeBttm
                    };

                    // Return concatenated observable of actions
                    return concat(
                        of(wellSurvey.setTailPipeLength(tailPipeLength)),
                        from(getTailPipeTZ(postData)).pipe(
                            map(response => wellSurvey.setTailPipeInsideTZ(Number(response)))
                        )
                    );
                }

                const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);

                // Skip calculation if essential values are missing
                if (!bottomOfEspSensorVal || !transitionZone) {
                    return of(wellSurvey.setTailPipeInsideTZ(0));
                }

                const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                    ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                    : 0;
                const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;
                const tailPipeBttm = tailPipeLengthValue === 0
                    ? bottomOfEspSensorVal + equipmentBelowLength
                    : bottomOfEspSensorVal + tailPipeLength;

                const postData: TailPipe = {
                    tailPipeDesander: schematicData.setDepth,
                    transitionZone,
                    tailPipeBttm
                };

                // Return single concat observable instead of multiple actions
                return concat(
                    of(wellSurvey.setTailPipeLength(tailPipeLength)),
                    of(wellSurvey.setTailPipeBottom(tailPipeBttm)),
                    from(getTailPipeTZ(postData)).pipe(
                        // Add distinctUntilChanged to prevent duplicate values
                        distinctUntilChanged(),
                        map(response => wellSurvey.setTailPipeInsideTZ(Number(response)))
                    )
                ).pipe(
                    // Add catchError here to handle any errors in the chain
                    catchError(error => {
                        console.error('Error in tail pipe calculation:', error);
                        return of(wellSurvey.setTailPipeInsideTZ(0));
                    })
                );
            } catch (error) {
                console.error('Error calculating Tail Pipe TZ:', error);
                return of(wellSurvey.setTailPipeInsideTZ(0));
            }
        })
    );
};

const calculateTailPipeBttmLength = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "wellSurvey/setTailPipeDesander",
            "wellSurvey/setBottomOfEspSensor",
            "calculations/setSchematicMiscData",
            "calculations/setSchematicData"
        ),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.calculations.schematicData)),
            state$.pipe(map((state: RootState) => state.schematic.ESPEquipmentsForBelow))
        ),
        map(([action, schematicData, equipmentForBelow]) => {
            try {
                let tailPipeBttmTwoDecimal = 0;
                const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);
                const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                    ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                    : 0;
                const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;

                if (tailPipeLengthValue === 0) {
                    tailPipeBttmTwoDecimal = bottomOfEspSensorVal + equipmentBelowLength;
                } else {
                    tailPipeBttmTwoDecimal = bottomOfEspSensorVal + tailPipeLength;
                }

                const tailPipeBttm = parseFloat(tailPipeBttmTwoDecimal.toFixed(2));
                return wellSurvey.setTailPipeBottom(tailPipeBttm);
            } catch (error) {
                console.error('Error calculating Tail Pipe Bottom:', error);
                return wellSurvey.setTailPipeBottom(0);
            }
        })
    );
};

// Update the combineEpics export
export const wellSurveyEpic = combineEpics(
    fetchWellSurveyEpic,
    clearWellSurveyEpic,
    calculateMaxDLS,
    calculateDepthMaxDLS,
    calculateTransitionZone,
    calculateTailPipeTZ,
    fetchExistingWellSurveyEpic,
    calculateTailPipeBttmLength
);

